import { Component, input, signal, inject, computed, output, ChangeDetectionStrategy } from '@angular/core';
import { ButtonDirective } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { DropdownModule } from "primeng/dropdown";
import { FormsModule } from "@angular/forms";
import { TooltipModule } from "primeng/tooltip";
import { CommonModule } from '@angular/common';
import { FileUploadHandlerEvent, FileUploadModule } from "primeng/fileupload";
import { FileUtilsService } from 'src/app/shared/services/file-utils-service';
import { FileQueueItem } from 'src/app/shared/interfaces/file-utils-interface';
import { LoginServices } from 'src/app/auth/login/services/login.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { CatalogoService } from 'src/app/shared/services/catalogo-service';
import { CatalogoInterface } from 'src/app/users/interfaces/proyecto.interface';

@Component({
  selector: 'app-subir-resolucion',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    DialogModule,
    DropdownModule,
    FormsModule,
    TooltipModule,
    CommonModule,
    FileUploadModule,
    ToastModule,
    PdfJsViewerModule,
  ],
  providers: [MessageService],
  templateUrl: './subir-resolucion.component.html',
  styleUrl: './subir-resolucion.component.scss',
})
export class SubirResolucionComponent {
  isVisible = input(false);
  projectId = input<string | number | null>(null);
  solicitudId = input<string | number | null>(null);

  readonly dialogClosed = output<boolean>();

  protected fileUtilsService = inject(FileUtilsService);
  private loginService = inject(LoginServices);
  private messageService = inject(MessageService);
  private catalogService = inject(CatalogoService);

  fileCatalog = this.catalogService.getCatalogo('archivo_tipo')
  fileForUpload = signal<File | null>(null);
  isUploading = signal(false);
  selectedTypeFile = signal<CatalogoInterface | null>(null);
  disabledUploadButton = computed(() => {
    return (!this.fileForUpload() && !this.selectedTypeFile()) || this.isUploading();
  });

  readonly actionButtonLabel = computed(() => {
    return this.isUploading() ? 'Subiendo...' : 'Subir';
  });

  close(emitEvent: boolean): void {
    this.fileForUpload.set(null);
    this.dialogClosed.emit(false);
  }

  handleTypeFileSelection(event: { value: CatalogoInterface | null }): void {
    this.selectedTypeFile.set(event.value);
  }

  handleFileSelectionToUpload(event: FileUploadHandlerEvent): void {
    const file = event.files[0];
    if (file) {
      this.fileForUpload.set(file);
    }
  }

  async submitFileForUpload(): Promise<void> {
    const fileToProcess = this.fileForUpload();
    const currentProjectId = this.projectId();
    const currentSolicitudId = this.solicitudId() ?? '0';

    if (!fileToProcess) {
      this.messageService.add({ severity: 'warn', summary: 'Advertencia', detail: 'Por favor, seleccione un archivo para subir.' });
      return;
    }
    if (!currentProjectId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'ID de proyecto no disponible.' });
      return;
    }
    if (this.isUploading()) {
      return;
    }
    if(!this.selectedTypeFile()){
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Seleccione el tipo de archivo.' });
        return;
    }

    this.isUploading.set(true);

    try {
      const hashStatus = await this.fileUtilsService.calcularSingleFileHash(fileToProcess);
      if (hashStatus !== 1) {
        throw new Error('No se pudo calcular el hash del archivo.');
      }

      const calculatedHash = this.fileUtilsService.hash;
      if (!calculatedHash) {
        throw new Error('Hash calculado no disponible.');
      }

      const sessionProfile = this.loginService.session()?.perfil;
      if (!sessionProfile?.uniqueId || !sessionProfile?.correo || !sessionProfile?.idInstitucion) {
        throw new Error('Información de sesión incompleta.');
      }

      const formData = new FormData();
      formData.append('idTipoArchivo', (this.selectedTypeFile()?.id ?? '0').toString());
      formData.append('nombre', fileToProcess.name);
      formData.append('file', fileToProcess);
      formData.append('hash', calculatedHash);
      formData.append('usuario', sessionProfile.usuario ?? 'sistema');
      formData.append('idProyecto', String(currentProjectId));
      formData.append('idSolicitud', String(currentSolicitudId));
      formData.append('idInstitucion', String(sessionProfile.idInstitucion));
      formData.append('uniqueId', sessionProfile.uniqueId);
      formData.append('correo', sessionProfile.correo);
      formData.append('documento', sessionProfile.documento);
      formData.append('idRequisito', '14')

      const fileItem: FileQueueItem = {
        frm: formData,
        hash: calculatedHash,
        progress: 0,
        status: 'pending'
      };

      this.fileUtilsService.upload(fileItem).subscribe({
        next: (event) => {
          if (event.type === 'progress') {
          } else if (event.type === 'response') {
            this.messageService.add({ severity: 'success', summary: 'Éxito', detail: `Archivo '${fileToProcess.name}' subido correctamente.` });
            this.close(true);
          }
        },
        error: (uploadError) => {
          console.error('Error durante la subida del archivo:', uploadError);
          const detail = uploadError?.status === 409
            ? 'El archivo ya existe o está duplicado.'
            : 'Ocurrió un error al subir el archivo.';
          this.messageService.add({ severity: 'error', summary: 'Error de Subida', detail });
        },
        complete: () => {
          this.isUploading.set(false);
        }
      });

    } catch (error: any) {
      console.error('Error en el proceso de preparación para la subida:', error);
      this.messageService.add({ severity: 'error', summary: 'Error de Preparación', detail: error.message || 'Ocurrió un error inesperado.' });
      this.isUploading.set(false);
    }
  }
}
